import { type FormEvent, useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  fetchRules,
  resetRule,
  type RuleClamp,
  type RuleKind,
  type RuleRowView,
  type RulesView,
  saveRule,
} from './api.ts';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { describeError } from './data.tsx';
import { Icon } from './Icon.tsx';
import { useToast } from './toast.tsx';
import { Badge, Notice, Panel, Skeleton } from './ui.tsx';

/**
 * Settings → **Format presets and tones** (PLAN §14.8, US-076).
 *
 * A format preset and a tone are files, and this panel edits the file — the
 * whole document, against the schema the boot validates the shipped ones with
 * — rather than a form of hand-picked fields. That is what makes "a new
 * platform does not need a code change" true: anything `format.schema.json`
 * accepts is a row, including one with a `preset` nobody here has heard of.
 *
 * Three things the panel is careful about, because the operator cannot see
 * them otherwise:
 *
 *   - **The clamp is shown, never applied in silence.** `rules/limits.json`
 *     `hard` is the floor below which nothing is legible and the ceiling above
 *     which nothing is watchable; a saved row that hit one comes back with a
 *     line saying what was asked for and what is in force.
 *   - **Overridden is a comparison, not a memory.** The server answers with the
 *     built-in file beside the row in force, so a row goes back to being
 *     un-marked the moment its numbers match the file again.
 *   - **A row in use cannot be deleted**, and the dialog names the contents —
 *     the whole of what the operator has to do next is in the sentence.
 */
function describeRules(view: RulesView): string {
  const overridden = [...view.formats, ...view.tones].filter((row) => row.overridden).length;
  return `${String(view.formats.length)} preset${view.formats.length === 1 ? '' : 's'}, ${String(view.tones.length)} tone${view.tones.length === 1 ? '' : 's'}, ${String(overridden)} overridden`;
}

export function RulesPanel() {
  const toast = useToast();
  const [view, setView] = useState<RulesView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [clamps, setClamps] = useState<readonly RuleClamp[]>([]);
  const [confirming, setConfirming] = useState<RuleRowView | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchRules(controller.signal)
      .then(setView)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  const edit = useCallback((row: RuleRowView): void => {
    setError(null);
    setClamps([]);
    setEditing({
      kind: row.kind,
      name: row.name,
      naming: false,
      text: JSON.stringify(row.document, null, 2),
    });
  }, []);

  /**
   * A new row starts as a copy of the first built-in of its table, because an
   * empty textarea against a schema with eight required properties is not an
   * invitation, it is a puzzle.
   */
  const add = useCallback(
    (kind: RuleKind): void => {
      if (view === null) return;
      const template = (kind === 'format' ? view.formats : view.tones)[0];
      if (template === undefined) return;
      setError(null);
      setClamps([]);
      setEditing({
        kind,
        name: '',
        naming: true,
        text: JSON.stringify(template.builtIn ?? template.document, null, 2),
      });
    },
    [view],
  );

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (editing === null) return;
    let document: unknown;
    try {
      document = JSON.parse(editing.text);
    } catch (cause) {
      setError(`This is not valid JSON: ${cause instanceof Error ? cause.message : String(cause)}`);
      return;
    }
    setBusy(true);
    setError(null);
    saveRule(editing.kind, editing.name.trim(), document)
      .then((saved) => {
        setView(saved.view);
        setClamps(saved.clamps);
        setEditing(null);
        toast.ok(
          saved.clamps.length === 0
            ? `${label(editing.kind)} “${editing.name.trim()}” saved. It applies to the next plan, lint and render.`
            : `${label(editing.kind)} “${editing.name.trim()}” saved, with ${String(saved.clamps.length)} value${saved.clamps.length === 1 ? '' : 's'} clamped to the hard limits.`,
        );
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const confirmReset = (): void => {
    const row = confirming;
    if (row === null) return;
    setBusy(true);
    setError(null);
    resetRule(row.kind, row.name)
      .then((next) => {
        setView(next);
        setClamps([]);
        setConfirming(null);
        toast.ok(
          row.resettable
            ? `${label(row.kind)} “${row.name}” is back to its built-in file.`
            : `${label(row.kind)} “${row.name}” removed.`,
        );
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
        setConfirming(null);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Panel
      id="rules"
      title="Format presets and tones"
      icon="stack"
      collapsible
      defaultOpen={false}
      summary={view === null ? (error === null ? 'loading…' : 'could not load') : describeRules(view)}
    >
      <p className="muted">
        The sizes and platform limits every render is measured against, and the creative postures
        a plan is written in.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {clamps.length > 0 && (
        <Notice kind="warn">
          <p>
            <strong>Clamped to the hard limits.</strong> These are the floors and ceilings in{' '}
            <code>rules/limits.json</code> that no tone, Settings row or plan header may cross, so
            the value in force is not the one that was typed:
          </p>
          <ul className="rules-list">
            {clamps.map((clamp) => (
              <li key={clamp.field}>
                <code>{clamp.field}</code>: asked for {clamp.asked}, in force {clamp.applied} (
                <code>hard.{clamp.bound}</code>)
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {view === null ? (
        <Skeleton lines={4} />
      ) : (
        <>
          <p className="muted">
            <Icon name="list" /> Precedence: {view.precedence.join(' → ')}. The bounds every row is
            clamped to are in <code>{view.limitsFile}</code>.
          </p>

          <RuleTable
            title="Format presets"
            columnLabel="Size, length and platforms"
            rows={view.formats}
            onEdit={edit}
            onReset={setConfirming}
            onAdd={() => {
              add('format');
            }}
            addLabel="Add a format"
          />
          <RuleTable
            title="Tones"
            columnLabel="What the posture is"
            rows={view.tones}
            onEdit={edit}
            onReset={setConfirming}
            onAdd={() => {
              add('tone');
            }}
            addLabel="Add a tone"
          />
        </>
      )}

      {editing !== null && (
        <form className="form" onSubmit={submit}>
          <div className="field">
            <label className="field__label" htmlFor="rule-name">
              {label(editing.kind)} name
            </label>
            <input
              id="rule-name"
              className="field__input field__input--narrow"
              value={editing.name}
              readOnly={!editing.naming}
              onChange={(event) => {
                setEditing({ ...editing, name: event.target.value });
              }}
              aria-describedby="rule-name-hint"
            />
            <p className="field__hint" id="rule-name-hint">
              Lower-case letters, digits and hyphens. It is the file name a plan header, a step and
              a render variant all name this row by, so an existing name saves over that row.
            </p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="rule-document">
              The row, as <code>{editing.kind}.schema.json</code> describes it
            </label>
            <textarea
              id="rule-document"
              className="field__input field__input--code"
              rows={18}
              spellCheck={false}
              value={editing.text}
              onChange={(event) => {
                setEditing({ ...editing, text: event.target.value });
              }}
            />
          </div>
          <div className="form__actions">
            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save row'}
            </button>
            <button
              className="button"
              type="button"
              disabled={busy}
              onClick={() => {
                setEditing(null);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={
          confirming === null
            ? ''
            : confirming.resettable
              ? `Reset ${confirming.name} to its built-in file?`
              : `Delete the ${confirming.kind} ${confirming.name}?`
        }
        confirmLabel={confirming?.resettable === true ? 'Reset to built-in' : 'Delete'}
        danger={confirming?.resettable === false}
        busy={busy}
        confirmDisabled={confirming !== null && !confirming.resettable && confirming.usedBy.length > 0}
        onConfirm={confirmReset}
        onCancel={() => {
          setConfirming(null);
        }}
      >
        {confirming !== null && <ResetExplanation row={confirming} />}
      </ConfirmDialog>
    </Panel>
  );
}

/** What the row being edited holds, before the server has had its say. */
interface Draft {
  kind: RuleKind;
  name: string;
  /** A new row names itself; an existing one cannot be renamed into another. */
  naming: boolean;
  text: string;
}

function label(kind: RuleKind): string {
  return kind === 'format' ? 'Format' : 'Tone';
}

/**
 * What confirming actually does, in the two cases that are not the same thing.
 * A built-in row cannot orphan a content — the file comes back — so only the
 * row Settings invented is refused, and then the contents are named, because
 * "change them first" is not actionable without knowing which.
 */
function ResetExplanation({ row }: { readonly row: RuleRowView }) {
  if (row.resettable) {
    return (
      <p>
        The numbers you saved are dropped and <code>{row.name}</code> goes back to the file this
        install ships. Contents already using it keep working — they name the row, not your copy of
        it.
      </p>
    );
  }
  if (row.usedBy.length === 0) {
    return (
      <p>
        There is no built-in <code>{row.name}</code> to go back to, so this removes the row. Nothing
        is using it.
      </p>
    );
  }
  return (
    <>
      <p>
        <strong>This {row.kind} cannot be deleted.</strong> It has no built-in file to fall back to,
        and {row.usedBy.length === 1 ? 'a content is' : `${String(row.usedBy.length)} contents are`}{' '}
        still naming it:
      </p>
      <ul className="rules-list">
        {row.usedBy.map((user) => (
          <li key={user.id}>{user.name}</li>
        ))}
      </ul>
      <p className="muted">
        Change {row.usedBy.length === 1 ? 'it' : 'them'} to another {row.kind} first.
      </p>
    </>
  );
}

/** One of the two tables: the rows, what each is, and the two things to do. */
function RuleTable({
  title,
  columnLabel,
  rows,
  onEdit,
  onReset,
  onAdd,
  addLabel,
}: {
  readonly title: string;
  readonly columnLabel: string;
  readonly rows: readonly RuleRowView[];
  readonly onEdit: (row: RuleRowView) => void;
  readonly onReset: (row: RuleRowView) => void;
  readonly onAdd: () => void;
  readonly addLabel: string;
}) {
  return (
    <section className="rules-table">
      <header className="rules-table__header">
        <h3 className="rules-table__title">{title}</h3>
        <button className="button button--small" type="button" onClick={onAdd}>
          <Icon name="plus" /> {addLabel}
        </button>
      </header>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">{columnLabel}</th>
              <th scope="col">In use</th>
              <th scope="col" className="table__actions">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name}>
                <td>
                  <code>{row.name}</code>{' '}
                  {row.overridden && (
                    <Badge
                      tone="review"
                      title={
                        row.resettable
                          ? 'This row differs from the built-in file it ships as.'
                          : 'This row has no built-in file: Settings is where it comes from.'
                      }
                    >
                      {row.resettable ? 'overridden' : 'added here'}
                    </Badge>
                  )}
                </td>
                <td className="muted">{row.summary}</td>
                <td className="muted">
                  {row.usedBy.length === 0
                    ? '—'
                    : row.usedBy.map((user) => user.name).join(', ')}
                </td>
                <td className="table__actions">
                  <button
                    className="button button--small"
                    type="button"
                    onClick={() => {
                      onEdit(row);
                    }}
                  >
                    Edit
                  </button>{' '}
                  {row.overridden && (
                    <button
                      className="button button--small"
                      type="button"
                      onClick={() => {
                        onReset(row);
                      }}
                    >
                      {row.resettable ? 'Reset to built-in' : 'Delete'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
